import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { GUIDE_INTRO, GUIDE_SECTIONS } from '../../lib/claire/guide';
import { cn } from '../../utils/cn';

/** "What can Claire do?" — an info button that opens the employee guide. */
export const ClaireGuideButton: React.FC<{ variant?: 'pill' | 'icon'; className?: string }> = ({ variant = 'icon', className }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title="What can Claire do?"
        aria-label="What can Claire do?"
        className={cn(
          variant === 'pill'
            ? 'shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium border border-[#d9c7a8] bg-[#faf6ec] text-[#5a4430] hover:bg-[#f3ead8] transition-colors'
            : 'p-1.5 rounded-lg text-gray-500 hover:text-[#5a4430] hover:bg-[#f3ead8] transition-colors',
          className,
        )}
      >
        <Info className={variant === 'pill' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        {variant === 'pill' && 'What can she do?'}
      </button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
            <div
              role="dialog"
              aria-label="What Claire can do"
              className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl bg-[#fffdf8] shadow-2xl border border-[#e6dccb]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-[#efe6d6]">
                <div>
                  <h2 className="text-lg font-semibold text-[#241813]">What Claire can do</h2>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#6a5a4a]">{GUIDE_INTRO}</p>
                </div>
                <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-[#8a7a6a] hover:bg-[#f3ead8]" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto px-6 py-4 space-y-5">
                {GUIDE_SECTIONS.map((s) => (
                  <section key={s.title}>
                    <h3 className="text-[14px] font-semibold text-[#241813]">{s.title}</h3>
                    {s.intro && <p className="mt-1 text-[13px] text-[#5a4a3a]">{s.intro}</p>}
                    {s.items && (
                      <ul className="mt-1.5 ml-4 list-disc space-y-1 text-[13px] leading-relaxed text-[#3a2c22]">
                        {s.items.map((i) => (
                          <li key={i}>{i}</li>
                        ))}
                      </ul>
                    )}
                    {s.examples && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {s.examples.map((x) => (
                          <span key={x} className="rounded-full bg-[#f3ead8] px-2.5 py-1 text-[12px] text-[#5a4430]">
                            “{x}”
                          </span>
                        ))}
                      </div>
                    )}
                    {s.note && <p className="mt-2 text-[12.5px] text-[#8a6a4a]">{s.note}</p>}
                  </section>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
